import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo, { JsonLd } from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/news";
import { getArticleById, getRelatedArticles } from "@/lib/news";

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);

  if (!article) {
    return (
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">文章不存在或已下线</h1>
          <p className="mt-3 text-sm text-foreground-600 font-label">
            这篇内容可能已经被移除，先回全部快讯看看别的吧。
          </p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回全部快讯
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedArticles(article, 3);
  const category = categories.find((item) => item.slug === article.category);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: "zh-CN",
    articleSection: article.categoryName,
    keywords: article.tags.join(","),
    author: { "@type": "Person", name: article.author },
    publisher: { "@type": "Organization", name: "独家吃瓜" },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://wxtmhb.cn/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://wxtmhb.cn/" },
      { "@type": "ListItem", position: 2, name: "全部快讯", item: "https://wxtmhb.cn/news" },
      {
        "@type": "ListItem",
        position: 3,
        name: article.categoryName,
        item: `https://wxtmhb.cn/category/${article.category}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: article.title,
        item: `https://wxtmhb.cn/article/${article.id}`,
      },
    ],
  };

  return (
    <>
      <Seo
        title={`${article.title} - 独家吃瓜`}
        description={`${article.excerpt} 独家吃瓜${article.categoryName}专栏持续跟进今日最新动态。`}
        keywords={`${article.tags.join(",")},${article.categoryName},独家吃瓜`}
        path={`/article/${article.id}`}
      />
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb
            items={[
              { label: "全部快讯", to: "/news" },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 lg:gap-12">
            <article className="min-w-0">
              <header>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-2.5 py-1 rounded bg-primary-500 text-background-50 text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    {article.categoryName}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded bg-secondary-100 text-secondary-700 text-xs font-label"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug tracking-tight">
                  {article.title}
                </h1>

                <p className="mt-4 text-base text-foreground-600 leading-relaxed font-label">
                  {article.excerpt}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 font-label pb-5 border-b border-background-200">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.time}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    阅读约 {article.readTime}
                  </span>
                </div>
              </header>

              <figure className="mt-6">
                <div className="w-full h-[240px] md:h-[400px] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - 独家吃瓜`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <figcaption className="mt-2.5 text-xs text-foreground-500 font-label">
                  图：{article.categoryName}相关画面 · 独家吃瓜整理
                </figcaption>
              </figure>

              <div className="mt-8 space-y-5">
                {article.content.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-[15px] md:text-base text-foreground-800 leading-[1.9] font-label"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 p-5 rounded-lg bg-accent-50 border border-accent-200">
                <p className="text-sm text-foreground-700 leading-relaxed font-label flex items-start gap-2">
                  <i className="ri-alert-line text-accent-600 mt-0.5"></i>
                  本文内容为公开信息整理，涉及尚未官方确认的部分仅作动态记录，后续如有更新会在本页同步。
                </p>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500 font-label">标签：</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/news?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1 rounded-full bg-background-100 border border-background-300 text-xs text-foreground-600 font-label hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1.5 text-sm font-label text-secondary-600 hover:text-secondary-700 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回全部快讯
                </Link>
              </div>
            </article>

            <aside className="space-y-6">
              <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">
                  相关推荐
                </h2>
                <div className="space-y-4">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <div className="w-20 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={item.image}
                          alt={item.title}
                          title={`${item.title} - 独家吃瓜`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors font-label">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-[11px] text-foreground-500 font-label">
                          {item.categoryName} · {item.date}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {category && (
                <div className="rounded-lg bg-secondary-950 p-5">
                  <h2 className="font-heading text-base font-bold text-background-50 mb-3">
                    当前栏目
                  </h2>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 rounded-lg bg-secondary-500 text-background-50 flex items-center justify-center text-lg">
                      <i className={category.icon}></i>
                    </span>
                    <span className="font-heading text-base font-semibold text-background-50">
                      {category.name}
                    </span>
                  </div>
                  <p className="text-xs text-background-300 leading-relaxed font-label mb-4">
                    {category.desc}
                  </p>
                  <Link
                    to={`/category/${category.slug}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-md bg-background-100/10 text-background-100 text-sm font-label hover:bg-background-100/20 transition-colors cursor-pointer"
                  >
                    进入栏目
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}

              <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">
                  其他分类
                </h2>
                <div className="space-y-1.5">
                  {categories.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/category/${item.slug}`}
                      className="flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-label text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${item.icon} text-primary-600`}></i>
                        {item.name}
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
              继续浏览
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        </div>
      </SiteLayout>
    </>
  );
}